/**
 * The roster a player builds (slice 11; moved here from the client's Quick Play screen in slice
 * 10a so the harness builds rosters with the same code): pure, no DOM. The player picks 11 from
 * the chosen generations (Species Clause), the sim proposes both depth charts from the slot grades
 * (packages/sim roster.ts, the same stats and position fit a battle uses), each Pokémon's move
 * set defaults to `draftMoveset` for its role and can be edited from its learnset, and the bag
 * holds up to `status.itemBagPerGame` bag items, each once (Item Clause). `rosterTeam` turns it
 * into the sim's own Team. Who it plays (a preset opponent, the AI level) is the screen's business.
 * A slot the player set by a swap is held (slice 11h): a pick change keeps its Pokémon and proposes
 * only the other slots; `recommendAll` (the Lineup button) releases every hold.
 *
 * Every function returns a new state of the type it was given; an edit the rules do not allow
 * returns the state unchanged.
 */
import {
  DEFENSE_SLOTS,
  OFFENSE_SLOTS,
  SLOT_POSITION,
  balance,
  combatantOf,
  itemOf,
  proposeChart,
  rosterProblems,
  type Combatant,
  type Creature,
  type DefenseSlot,
  type Move,
  type OffenseSlot,
  type Position,
  type RosterProblem,
  type RosterSlot,
  type Side,
  type Team,
  type TeamDraft,
  type Balance,
} from '@tdmons/sim';
import { draftMoveset } from './moveset';
import type { CreaturePack } from './pack';
import { listedPositions } from './positions';

export interface RosterPick {
  id: string;
  moves: string[];
  /** The player changed the move set: Recommend keeps it. */
  edited: boolean;
}

export interface RosterDraft {
  gens: number[];
  /** Roster order; chart entries are indexes into it. */
  picks: RosterPick[];
  offense: Partial<Record<OffenseSlot, number>>;
  defense: Partial<Record<DefenseSlot, number>>;
  /**
   * The slots the player set by a swap (slice 11h), in slot order, each once: a pick change keeps
   * each one's Pokémon and proposes the rest; `recommendAll` (the Lineup button) releases them all.
   * The analogue of `RosterPick.edited`. A hold is on the slot, with whoever is in it.
   */
  held: { offense: OffenseSlot[]; defense: DefenseSlot[] };
  bag: string[];
}

/** One end of a two-tap swap: a slot in the chart, or a roster index (the bench). */
export type SwapRef = { slot: RosterSlot } | { index: number };

// The shipped sizes, for the roster screen's labels. The rules below read the balance they are
// handed, so a harness sweep through a copy builds its rosters on that copy.
export const ROSTER_SIZE = balance.roster.starters + balance.roster.bench;
export const BAG_SIZE = balance.status.itemBagPerGame;
const rosterSize = (b: Balance) => b.roster.starters + b.roster.bench;
const SLOTS: Record<Side, readonly RosterSlot[]> = {
  offense: OFFENSE_SLOTS,
  defense: DEFENSE_SLOTS,
};

/** The pack, indexed for roster building. */
export interface PackIndex {
  gens: number[];
  creatures: Creature[];
  byId: ReadonlyMap<string, Creature>;
  moves: ReadonlyMap<string, Move>;
}

export function indexPack(pack: CreaturePack): PackIndex {
  return {
    gens: [...pack.gens],
    creatures: pack.creatures,
    byId: new Map(pack.creatures.map((c) => [c.id, c])),
    moves: pack.moves,
  };
}

/** The bag items there are (balance.json `items`, slot `bag`), in file order. */
export function bagItemIds(b: Balance = balance): string[] {
  return Object.keys(b.items).filter((id) => itemOf(id, b)?.slot === 'bag');
}

/** A fresh roster: nobody picked, the recommended bag. */
export function emptyState(gens: readonly number[], b: Balance = balance): RosterDraft {
  return {
    gens: [...gens],
    picks: [],
    offense: {},
    defense: {},
    held: { offense: [], defense: [] },
    bag: bagItemIds(b).slice(0, b.status.itemBagPerGame),
  };
}

const creatureOf = (pack: PackIndex, id: string): Creature => {
  const c = pack.byId.get(id);
  if (!c) throw new Error(`${id} is not in the loaded pack`);
  return c;
};

export function positionsOf(creature: Creature, b: Balance): Position[] {
  return listedPositions(creature.baseStats, b);
}

/** Pick `i` as a Combatant, with its move set as the pack has each move. */
export function combatant(
  state: RosterDraft,
  pack: PackIndex,
  i: number,
  b: Balance = balance,
): Combatant {
  const pick = state.picks[i];
  if (!pick) throw new Error(`no pick ${i}`);
  const creature = creatureOf(pack, pick.id);
  const moves = pick.moves.flatMap((id) => {
    const m = pack.moves.get(id);
    return m ? [m] : [];
  });
  return combatantOf(creature, moves, positionsOf(creature, b), b);
}

export function roster(state: RosterDraft, pack: PackIndex, b: Balance): Combatant[] {
  return state.picks.map((_, i) => combatant(state, pack, i, b));
}

/** The slot pick `i` holds in a chart, if any. */
export function slotOf(state: RosterDraft, side: Side, i: number): RosterSlot | undefined {
  const chart = state[side] as Partial<Record<RosterSlot, number>>;
  return SLOTS[side].find((s) => chart[s] === i);
}

/** The position pick `i` plays: its offense slot, else its defense slot, else its first listed. */
export function rolePosition(state: RosterDraft, pack: PackIndex, i: number, b: Balance): Position {
  const slot = slotOf(state, 'offense', i) ?? slotOf(state, 'defense', i);
  if (slot) return SLOT_POSITION[slot];
  const pick = state.picks[i];
  return (pick && positionsOf(creatureOf(pack, pick.id), b)[0]) ?? 'BENCH';
}

function drafted(pack: PackIndex, id: string, position: Position): string[] {
  return draftMoveset(creatureOf(pack, id), pack.moves, position).map((m) => m.id);
}

/**
 * The proposal: both charts from the slot grades, then every move set the player has not edited
 * redrafted for its Pokémon's role in them. Keeps holds (slice 11h): a held slot keeps its Pokémon
 * and the other slots are proposed from the Pokémon not held on that side (`proposeChart` on the
 * rest of the roster and the open slots, indexes mapped back); the Lineup button is `recommendAll`.
 * With no holds the charts are exactly `proposeCharts`, so the harness's rosters never move.
 */
export function recommend<S extends RosterDraft>(
  state: S,
  pack: PackIndex,
  b: Balance = balance,
): S {
  const combatants = roster(state, pack, b);
  const side = <T extends RosterSlot>(
    slots: readonly T[],
    chart: Partial<Record<T, number>>,
    held: readonly T[],
  ): { chart: Partial<Record<T, number>>; held: T[] } => {
    // A hold counts only where the chart has a pick there.
    const kept = slots.filter((s) => {
      const i = chart[s];
      return held.includes(s) && i !== undefined && i < combatants.length;
    });
    const taken = new Set(kept.map((s) => chart[s]));
    const free = combatants.flatMap((_, i) => (taken.has(i) ? [] : [i]));
    const open = slots.filter((s) => !kept.includes(s));
    const proposed = proposeChart(
      free.map((i) => combatants[i] as Combatant),
      open,
      b,
    );
    const out: Partial<Record<T, number>> = {};
    for (const s of slots) {
      const p = proposed[s];
      if (kept.includes(s)) out[s] = chart[s];
      else if (p !== undefined) out[s] = free[p];
    }
    return { chart: out, held: kept };
  };
  const offense = side(OFFENSE_SLOTS, state.offense, state.held.offense);
  const defense = side(DEFENSE_SLOTS, state.defense, state.held.defense);
  const next: S = {
    ...state,
    offense: offense.chart,
    defense: defense.chart,
    held: { offense: offense.held, defense: defense.held },
  };
  next.picks = state.picks.map((p, i) =>
    p.edited ? p : { ...p, moves: drafted(pack, p.id, rolePosition(next, pack, i, b)) },
  );
  return next;
}

/** The Lineup button: every hold released, then the proposal for every slot on both sides. */
export function recommendAll<S extends RosterDraft>(
  state: S,
  pack: PackIndex,
  b: Balance = balance,
): S {
  return recommend({ ...state, held: { offense: [], defense: [] } }, pack, b);
}

export const isPicked = (state: RosterDraft, id: string) => state.picks.some((p) => p.id === id);

/** Tap a Pokémon in the grid: add it (11 at most, each species once), or take it out. */
export function togglePick<S extends RosterDraft>(
  state: S,
  pack: PackIndex,
  id: string,
  b: Balance = balance,
): S {
  const at = state.picks.findIndex((p) => p.id === id);
  if (at >= 0) return unpick(state, pack, at, b);
  if (state.picks.length >= rosterSize(b) || !pack.byId.has(id)) return state;
  const creature = creatureOf(pack, id);
  const pick: RosterPick = {
    id,
    moves: drafted(pack, id, positionsOf(creature, b)[0] ?? 'BENCH'),
    edited: false,
  };
  return recommend({ ...state, picks: [...state.picks, pick] }, pack, b);
}

export function unpick<S extends RosterDraft>(
  state: S,
  pack: PackIndex,
  at: number,
  b: Balance,
): S {
  if (at < 0 || at >= state.picks.length) return state;
  // Slice 11h: every chart entry above `at` moves down one and its own entry is dropped (a hold on
  // that slot is released); then the proposal fills what is open, the other holds kept.
  const remap = <T extends RosterSlot>(
    chart: Partial<Record<T, number>>,
  ): Partial<Record<T, number>> => {
    const out: Partial<Record<T, number>> = {};
    for (const [slot, i] of Object.entries(chart) as [T, number][]) {
      if (i !== at) out[slot] = i > at ? i - 1 : i;
    }
    return out;
  };
  const offense = remap(state.offense);
  const defense = remap(state.defense);
  return recommend(
    {
      ...state,
      picks: state.picks.filter((_, i) => i !== at),
      offense,
      defense,
      held: {
        offense: state.held.offense.filter((s) => offense[s] !== undefined),
        defense: state.held.defense.filter((s) => defense[s] !== undefined),
      },
    },
    pack,
    b,
  );
}

/**
 * The second tap of a swap on one chart. Two slots trade Pokémon (an empty slot takes the other's);
 * a slot and a Pokémon: if it is already in this chart the two trade slots, else it goes into the
 * slot and the one there goes to this side's bench. Two bench Pokémon: nothing to do.
 *
 * The swap sets the holds (slice 11h): a hold is on the slot, with whoever is in it. A slot the
 * swap filled is held (two slots trading: both; a slot and a bench Pokémon: the slot, the one it
 * displaced is free to be proposed elsewhere; a move into an empty slot: the destination), and a
 * slot it emptied is released.
 */
export function swap<S extends RosterDraft>(state: S, side: Side, a: SwapRef, b: SwapRef): S {
  const chart: Partial<Record<RosterSlot, number>> = { ...state[side] };
  const set = (slot: RosterSlot, i: number | undefined) => {
    if (i === undefined) delete chart[slot];
    else chart[slot] = i;
  };
  const slotFor = (r: SwapRef): RosterSlot | undefined =>
    'slot' in r ? r.slot : SLOTS[side].find((s) => chart[s] === r.index);
  const sa = slotFor(a);
  const sb = slotFor(b);
  const touched: RosterSlot[] = [];
  if (sa && sb) {
    if (sa === sb) return state;
    const ia = chart[sa];
    set(sa, chart[sb]);
    set(sb, ia);
    touched.push(sa, sb);
  } else if (sa && 'index' in b) {
    set(sa, b.index);
    touched.push(sa);
  } else if (sb && 'index' in a) {
    set(sb, a.index);
    touched.push(sb);
  } else return state;
  const was: readonly RosterSlot[] = state.held[side];
  const held = SLOTS[side].filter((s) =>
    touched.includes(s) ? chart[s] !== undefined : was.includes(s),
  );
  return { ...state, [side]: chart, held: { ...state.held, [side]: held } };
}

/** The moves pick `i` can know here: its learnset, as the pack has each, by name. */
export function learnable(state: RosterDraft, pack: PackIndex, i: number): Move[] {
  const pick = state.picks[i];
  if (!pick) return [];
  return creatureOf(pack, pick.id)
    .learnset.flatMap((id) => {
      const m = pack.moves.get(id);
      return m && m.effect.kind !== 'none' ? [m] : [];
    })
    .sort((x, y) => (x.name < y.name ? -1 : x.name > y.name ? 1 : 0));
}

/** Put `moveId` in move slot `at` of pick `i` (`at` = its move count adds one, up to 4). */
export function setMove<S extends RosterDraft>(
  state: S,
  pack: PackIndex,
  i: number,
  at: number,
  moveId: string,
): S {
  const pick = state.picks[i];
  if (!pick || at < 0 || at > Math.min(pick.moves.length, 3)) return state;
  const creature = creatureOf(pack, pick.id);
  if (!creature.learnset.includes(moveId) || !pack.moves.has(moveId)) return state;
  if (pick.moves.some((m, k) => m === moveId && k !== at)) return state;
  const moves = [...pick.moves];
  moves[at] = moveId;
  return withPick(state, i, { ...pick, moves, edited: true });
}

/** Drop move slot `at` of pick `i`; a Pokémon keeps at least one move. */
export function removeMove<S extends RosterDraft>(state: S, i: number, at: number): S {
  const pick = state.picks[i];
  if (!pick || pick.moves.length <= 1 || at < 0 || at >= pick.moves.length) return state;
  return withPick(state, i, {
    ...pick,
    moves: pick.moves.filter((_, k) => k !== at),
    edited: true,
  });
}

/** Pick `i`'s move set back to the recommended one for its role. */
export function resetMoves<S extends RosterDraft>(
  state: S,
  pack: PackIndex,
  i: number,
  b: Balance = balance,
): S {
  const pick = state.picks[i];
  if (!pick) return state;
  const moves = drafted(pack, pick.id, rolePosition(state, pack, i, b));
  return withPick(state, i, { ...pick, moves, edited: false });
}

function withPick<S extends RosterDraft>(state: S, i: number, pick: RosterPick): S {
  return { ...state, picks: state.picks.map((p, k) => (k === i ? pick : p)) };
}

/** Tap a bag item: in or out; a full bag takes nothing more, and no item twice. */
export function toggleBag<S extends RosterDraft>(state: S, item: string, b: Balance = balance): S {
  if (state.bag.includes(item)) return { ...state, bag: state.bag.filter((x) => x !== item) };
  if (state.bag.length >= b.status.itemBagPerGame || itemOf(item, b)?.slot !== 'bag') return state;
  return { ...state, bag: [...state.bag, item] };
}

/** "Team Electabuzz", or "Team Electabuzz (You)" when the opponent has that name too. */
export function teamName(state: RosterDraft, pack: PackIndex, opponentName: string): string {
  const qb = state.offense.QB;
  const pick = qb === undefined ? undefined : state.picks[qb];
  const name = pick ? `Team ${creatureOf(pack, pick.id).name}` : 'Your team';
  return name === opponentName ? `${name} (You)` : name;
}

/** The player's team as the sim checks it, charts possibly not full yet; always team A. */
export function teamDraft(
  state: RosterDraft,
  pack: PackIndex,
  opponentName = '',
  b: Balance = balance,
): TeamDraft {
  return {
    id: 'A',
    name: teamName(state, pack, opponentName),
    roster: roster(state, pack, b),
    offense: { ...state.offense },
    defense: { ...state.defense },
    bag: [...state.bag],
  };
}

export function problems(
  state: RosterDraft,
  pack: PackIndex,
  b: Balance = balance,
): RosterProblem[] {
  return rosterProblems(teamDraft(state, pack, '', b), (id) => pack.byId.get(id)?.learnset, b);
}

/** The player's team for a game, as team A; throws on a roster `problems` does not pass. */
export function rosterTeam(
  state: RosterDraft,
  pack: PackIndex,
  opponentName = '',
  b: Balance = balance,
): Team {
  const found = problems(state, pack, b);
  if (found.length > 0) throw new Error(`the roster is not ready: ${found[0]?.code}`);
  return teamDraft(state, pack, opponentName, b) as Team;
}
